import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';
import type { EChartsOption } from 'echarts';
import type { CallbackDataParams } from 'echarts/types/dist/shared';
import UiChart from '@/components/UiChart.vue';
import type { AuraChartBlock as ChartBlock } from '@/types/models/AuraResponseBlocksModel';
import {
    formatAuraDateLabel,
    formatAuraMetric,
    isAuraDurationUnit,
} from '@/utils/auraPresentation';
import AuraChartTable from './AuraChartTable.vue';
import { useReducedMotion } from '@/composables/useReducedMotion';
import { useAuraResponsive } from '@/composables/useAuraResponsive';
import { AURA_CHART_PALETTE } from '@/utils/auraChartPalette';
import { buildAuraHeatmapOption } from '@/utils/auraHeatmapOption';

interface Props {
    chart: ChartBlock;
    selectedLabel?: string | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AuraChartBlock',
  props: {
    chart: { type: Object, required: true },
    selectedLabel: { type: [String, null], required: false }
  },
  emits: ["select"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

const props = __props;
const emit = __emit;
const { reducedMotion } = useReducedMotion();
const { isCompactLandscape } = useAuraResponsive();

const labels = computed(() =>
    (Array.isArray(props.chart.labels) ? props.chart.labels : []).map(String),
);
const data = computed(() => (Array.isArray(props.chart.data) ? props.chart.data : []));
const chartSeries = computed(() =>
    (Array.isArray(props.chart.series) ? props.chart.series : []).filter(
        (item) =>
            item &&
            typeof item.name === 'string' &&
            Array.isArray(item.data) &&
            item.data.length === labels.value.length,
    ),
);
const heatmapYLabels = computed(() =>
    (Array.isArray(props.chart.yLabels) ? props.chart.yLabels : []).map(String),
);
const heatmapPoints = computed(() =>
    (Array.isArray(props.chart.points) ? props.chart.points : []).filter(
        (point): point is [number, number, number] =>
            Array.isArray(point) &&
            point.length === 3 &&
            point.every((value) => Number.isFinite(Number(value))),
    ),
);
const rows = computed(() =>
    labels.value.map((label, index) => ({ label, value: data.value[index] })),
);
const tableRows = computed(() =>
    Array.isArray(props.chart.rows)
        ? props.chart.rows.filter(
              (row): row is Record<string, unknown> =>
                  Boolean(row) && typeof row === 'object' && !Array.isArray(row),
          )
        : [],
);
const tableColumns = computed(() =>
    Array.isArray(props.chart.columns) ? props.chart.columns.map(String).filter(Boolean) : [],
);
const validRows = computed(() => rows.value.filter((row) => typeof row.value === 'number'));
const chartType = computed(() =>
    String(props.chart.type || 'table')
        .trim()
        .toLowerCase(),
);
const chartRenderKey = computed(
    () =>
        `${chartType.value}:${props.selectedLabel || ''}:${labels.value.join('|')}:${data.value.join('|')}:${heatmapPoints.value
            .map((point) => point.join(','))
            .join('|')}`,
);
const chartHeight = computed(() => {
    if (chartType.value === 'bar') {
        return isCompactLandscape.value
            ? `${Math.max(190, labels.value.length * 28 + 44)}px`
            : `${Math.max(280, labels.value.length * 44 + 72)}px`;
    }
    if (chartType.value === 'heatmap') return isCompactLandscape.value ? '260px' : '430px';
    if (chartType.value === 'pie' || chartType.value === 'radar') {
        return isCompactLandscape.value ? '240px' : '360px';
    }
    return isCompactLandscape.value ? '210px' : '320px';
});
const chartTitle = computed(() => {
    if (props.chart.title) return props.chart.title;
    const titles: Record<string, string> = {
        area: 'Evolução no período',
        bar: 'Distribuição dos resultados',
        pie: 'Participação nos resultados',
        radar: 'Comparativo dos resultados',
        heatmap: 'Distribuição por dia e hora',
        table: 'Dados detalhados',
    };
    return titles[chartType.value] || 'Visualização dos dados';
});
const shouldUseTable = computed(
    () =>
        chartType.value === 'table' ||
        validRows.value.length === 0 ||
        validRows.value.length !== rows.value.length ||
        labels.value.length !== data.value.length ||
        (chartType.value === 'heatmap' &&
            (heatmapYLabels.value.length === 0 ||
                heatmapPoints.value.length !== labels.value.length * heatmapYLabels.value.length)),
);

const palette = AURA_CHART_PALETTE;
const primaryGradient = {
    type: 'linear',
    x: 0,
    y: 0,
    x2: 1,
    y2: 0,
    colorStops: [
        { offset: 0, color: '#48DDC3' },
        { offset: 1, color: '#25BFA8' },
    ],
};
const numericValues = computed(() => validRows.value.map((row) => row.value as number));
const total = computed(() =>
    numericValues.value.reduce((sum, value) => sum + Math.max(value, 0), 0),
);

const formatValue = (value: unknown) => formatAuraMetric(value, props.chart.unit);
const formatAxisValue = (value: unknown) => formatValue(Number(value));

const tooltipFormatter = (params: CallbackDataParams | CallbackDataParams[]) => {
    if (Array.isArray(params) && chartSeries.value.length > 0) {
        const title = formatAuraDateLabel(
            String(params[0]?.axisValueLabel || params[0]?.name || ''),
        );
        const values = params
            .map((item) => {
                const value = Array.isArray(item.value) ? item.value[1] : item.value;
                return `${item.marker || ''}${item.seriesName}: <strong>${formatValue(value)}</strong>`;
            })
            .join('<br/>');
        return `<strong>${title}</strong><br/>${values}`;
    }

    const item = Array.isArray(params) ? params[0] : params;
    const index = Number(item?.dataIndex ?? 0);
    const label = formatAuraDateLabel(labels.value[index] || String(item?.name || ''));
    const value = formatValue(data.value[index]);
    const percentage =
        props.chart.showShare === true && total.value > 0 && typeof data.value[index] === 'number'
            ? ` (${(((data.value[index] as number) / total.value) * 100).toFixed(1)}%)`
            : '';
    return `<strong>${label}</strong><br/>${value}${percentage}`;
};

const chartOption = computed<EChartsOption>(() => {
    const common = {
        animation: !reducedMotion.value,
        animationDuration: 350,
        animationDurationUpdate: 250,
        tooltip: {
            trigger: ['pie', 'radar'].includes(chartType.value) ? 'item' : 'axis',
            formatter: tooltipFormatter,
            backgroundColor: '#242B59',
            borderWidth: 0,
            textStyle: { color: '#FFFFFF', fontSize: 12 },
            extraCssText: 'border-radius: 10px; box-shadow: 0 8px 24px rgba(36,43,89,.18);',
        },
        grid: { top: 24, right: 20, bottom: 34, left: 90, containLabel: true },
    } satisfies EChartsOption;

    if (chartType.value === 'heatmap') {
        return buildAuraHeatmapOption({
            animation: !reducedMotion.value,
            labels: labels.value,
            yLabels: heatmapYLabels.value,
            points: heatmapPoints.value,
            unit: props.chart.unit,
        });
    }

    if (chartType.value === 'pie') {
        return {
            ...common,
            legend: {
                type: 'scroll',
                bottom: 0,
                left: 'center',
                icon: 'roundRect',
                itemGap: 16,
                textStyle: { color: '#626468', fontSize: 11 },
            },
            series: [
                {
                    type: 'pie',
                    selectedMode: 'single',
                    selectedOffset: 18,
                    radius: ['40%', '70%'],
                    center: ['50%', '46%'],
                    data: validRows.value.map((row, index) => ({
                        name: row.label,
                        value: row.value,
                        itemStyle: {
                            color: palette[index % palette.length],
                        },
                        selected: row.label === props.selectedLabel,
                    })),
                    label: {
                        show: true,
                        formatter: props.chart.showShare === true ? '{b}\n{d}%' : '{b}',
                        color: '#242B59',
                        fontSize: 11,
                        fontWeight: 600,
                    },
                    labelLine: {
                        show: true,
                        length: 12,
                        length2: 8,
                        lineStyle: { color: '#B9C2CC' },
                    },
                    hoverAnimation: true,
                    emphasis: {
                        focus: 'self',
                        scale: true,
                        scaleSize: 10,
                        itemStyle: {
                            shadowBlur: 18,
                            shadowColor: 'rgba(36,43,89,.24)',
                        },
                    },
                },
            ],
        };
    }

    if (chartType.value === 'radar') {
        const maximum = Math.max(...numericValues.value, 1);
        return {
            ...common,
            radar: {
                indicator: labels.value.map((name) => ({ name, max: maximum })),
                radius: '64%',
                splitNumber: 4,
                axisName: { color: '#626468', fontSize: 11 },
                splitArea: {
                    areaStyle: { color: ['rgba(72,221,195,.03)', 'rgba(36,43,89,.03)'] },
                },
                axisLine: { lineStyle: { color: '#E5E5E5' } },
                splitLine: { lineStyle: { color: '#E5E5E5' } },
            },
            series: [
                {
                    type: 'radar',
                    data: [{ value: numericValues.value, name: props.chart.title || 'Resultado' }],
                    symbol: 'circle',
                    symbolSize: 6,
                    lineStyle: { color: palette[1], width: 2 },
                    itemStyle: { color: palette[1] },
                    areaStyle: { color: 'rgba(36,43,89,.18)' },
                    emphasis: { lineStyle: { width: 3 } },
                },
            ],
        } as EChartsOption;
    }

    const isArea = chartType.value === 'area';
    const isLine = chartType.value === 'line' || isArea;
    const isBar = chartType.value === 'bar';
    const lineSeries =
        chartSeries.value.length > 0
            ? chartSeries.value.map((item, index) => ({
                  name: item.name,
                  type: isBar ? ('bar' as const) : ('line' as const),
                  data: item.data,
                  stack: isBar && props.chart.stacked ? 'total' : undefined,
                  smooth: !isBar,
                  showSymbol: !isBar,
                  symbol: 'circle',
                  symbolSize: 7,
                  lineStyle: { color: palette[index % palette.length], width: 3 },
                  itemStyle: {
                      color: '#FFFFFF',
                      borderColor: palette[index % palette.length],
                      borderWidth: 3,
                  },
                  emphasis: {
                      focus: 'series' as const,
                      scale: true,
                      lineStyle: { width: 4 },
                      itemStyle: {
                          shadowBlur: 14,
                          shadowColor: `${palette[index % palette.length]}55`,
                      },
                  },
              }))
            : null;
    return {
        ...common,
        legend:
            lineSeries && lineSeries.length > 1
                ? {
                      type: 'scroll',
                      top: 0,
                      left: 'center',
                      icon: 'circle',
                      itemGap: 18,
                      textStyle: { color: '#626468', fontSize: 11 },
                  }
                : undefined,
        grid: {
            ...common.grid,
            top: lineSeries && lineSeries.length > 1 ? 48 : 24,
        },
        xAxis: isLine
            ? {
                  type: 'category',
                  data: labels.value,
                  boundaryGap: false,
                  axisLabel: {
                      hideOverlap: true,
                      color: '#626468',
                      formatter: formatAuraDateLabel,
                  },
                  axisLine: { lineStyle: { color: '#D9E0E7' } },
                  axisTick: { show: false },
              }
            : {
                  type: 'value',
                  axisLabel: { color: '#626468', formatter: formatAxisValue },
              },
        yAxis: isLine
            ? {
                  type: 'value',
                  minInterval: isAuraDurationUnit(props.chart.unit) ? undefined : 1,
                  axisLabel: { color: '#626468', formatter: formatAxisValue },
                  axisLine: { show: false },
                  axisTick: { show: false },
                  splitLine: { lineStyle: { color: '#E7EBEF' } },
              }
            : {
                  type: 'category',
                  data: labels.value,
                  axisLabel: {
                      width: 140,
                      overflow: 'truncate',
                      formatter: formatAuraDateLabel,
                  },
              },
        series: lineSeries || [
            {
                type: isLine ? 'line' : 'bar',
                stack: props.chart.stacked ? 'total' : undefined,
                data: isBar
                    ? numericValues.value.map((value) => ({
                          value,
                          itemStyle: {
                              color: primaryGradient,
                              borderRadius: [0, 7, 7, 0],
                              shadowBlur: 8,
                              shadowColor: 'rgba(72,221,195,.18)',
                          },
                      }))
                    : numericValues.value,
                smooth: isLine,
                showSymbol: isLine,
                symbol: 'circle',
                symbolSize: isLine ? 7 : undefined,
                barWidth: isBar ? '58%' : undefined,
                lineStyle: isLine ? { color: palette[0], width: 3 } : undefined,
                areaStyle: isArea ? { color: 'rgba(72,221,195,.18)' } : undefined,
                itemStyle: {
                    color: isLine ? palette[0] : primaryGradient,
                    borderRadius: isLine ? 0 : [0, 7, 7, 0],
                    shadowBlur: isLine ? 0 : 8,
                    shadowColor: isLine ? 'transparent' : 'rgba(72,221,195,.18)',
                },
                emphasis: {
                    focus: 'series',
                    scale: true,
                    itemStyle: { shadowBlur: 14, shadowColor: 'rgba(36,43,89,.16)' },
                },
            },
        ],
    } as EChartsOption;
});

const __returned__ = { props, emit, reducedMotion, isCompactLandscape, labels, data, chartSeries, heatmapYLabels, heatmapPoints, rows, tableRows, tableColumns, validRows, chartType, chartRenderKey, chartHeight, chartTitle, shouldUseTable, palette, primaryGradient, numericValues, total, formatValue, formatAxisValue, tooltipFormatter, chartOption, UiChart, AuraChartTable }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})